import { BoardMembers } from "@/components/BoardMember";
import FollowButton from "@/components/FollowButton";
import { followType } from "@/components/FollowButton/operationButton";
import HBIcon from "@/components/HBIcon";
import Image from "@/components/Image";
import ShareDropdown from "@/components/ShareDropdown";
import { UserGuideTooltip } from "@/components/UserGuideTooltip";
import { PRIVATETYPE } from "@/constants/board";
import { LOCAL_STORAGE_KEY } from "@/constants/localStorageKey";
import useAppStore from "@/context/store";
import reporter from "@/tracker/report/reporter";
import { getPage } from "@/tracker/shared/global";
import { canCollabBoardMng } from "@/utils/board";
import displayNum from "@/utils/displayNum";
import getTimeDuration from "@/utils/getTimeDuration";
import { useEnableMultiBoard } from "@/utils/hooks/useMultiBoard";
import imageUrl from "@/utils/imageUrl";
import strEllipsis from "@/utils/strEllipsis";
import { useMemoizedFn } from "ahooks";
import { Button } from "antd";
import classNames from "classnames";
import { useRouter } from "next/router";
import { useMemo } from "react";
import { Else, If, Then, When } from "react-if";
import styles from "./index.module.less";

interface IProps {
  tab: string;
  board: IBoardInfo;
  setBoard: (board: IBoardInfo) => void;
  liked: boolean;
  following: boolean;
  goBatch: () => void;
  onBoardEdit: () => void;
  onToggleLike: () => void;
  onToggleFollow: () => void;
  onChangeTab: (tab: string) => void;
  onCategorySelect: (category: string) => void;
  enableDragSort?: boolean;
  members: IUser[];
  mutateMembers: (members: IUser[] | ((prev: IUser[]) => IUser[])) => void;
  onExitBoard: () => void;
  onRemoveMember: (user: IUser) => void;
}

export function BoardPageHeader({
  board,
  setBoard,
  goBatch,
  onBoardEdit,
  liked,
  following,
  onToggleFollow,
  onToggleLike,
  onChangeTab,
  onCategorySelect,
  enableDragSort,
  members,
  mutateMembers,
  onExitBoard,
  onRemoveMember,
}: IProps) {
  const { user, listCacheMap } = useAppStore(({ user, listCacheMap }) => ({
    user,
    listCacheMap,
  }));

  const report = useMemoizedFn((buttonName) => {
    // 埋点上报
    try {
      const pageName = getPage();
      const trackData = {
        event_id: 39008,
        event_name: "button_click",
        button_name: buttonName,
        board_id: boardId,
        ...pageName,
      };

      reporter(trackData, { bd_disabled: true });
    } catch (e) {
      // do nothing
    }
  });

  const open = useMemoizedFn((_, buttomName) => {
    report(buttomName);
  });

  const setFollowUser = (value) => {
    setBoard({ ...board, user_following: value });
  };

  const router = useRouter();
  const { boardId, tab } = router.query;

  // 奇葩的待归类画板
  const isSpecial = board?.is_private === 2;

  const pics = useMemo(() => {
    return board?.pins?.map((pin) => imageUrl(pin.file)) || [];
  }, [board]);

  const { enable } = useEnableMultiBoard();

  const isOwner = useMemo(() => {
    if (!board?.user?.user_id || !user?.user_id) {
      return false;
    }
    return board?.user?.user_id === user?.user_id;
  }, [board, user]);

  const enableMemberMutate = useMemo(() => {
    return enable && !board?.extra?.is_creation && isOwner;
  }, [enable, board?.extra?.is_creation, isOwner]);

  // 上榜信息
  const listInfo = useMemo(() => {
    // 如果不在榜上，返回空数组
    if (!listCacheMap || !board?.board_id || !listCacheMap[board?.board_id]) {
      return [];
    } else {
      // 反转数组，因为最后面的是最新的，放在最前面
      return listCacheMap[board?.board_id].reverse();
    }
  }, [listCacheMap, board]);

  const canEditBoard = useMemo(() => {
    return canCollabBoardMng(board, user);
  }, [board, user]);

  const categories = useAppStore((store) => store.categories);

  return (
    <div className={styles.header}>
      <div className={styles.top}>
        <div className={styles.left}>
          <h1 className={styles.title}>{board?.title}</h1>
        </div>
        <div className={styles.right}>
          <If condition={isSpecial}>
            <Then>
              <ShareDropdown
                placement="bottomCenter"
                pics={pics}
                title={`这个来自「花瓣网」的画板不错哦，有很多${strEllipsis(
                  board?.title,
                  45
                )}相关的设计灵感，来看看：`}
              >
                <Button
                  className={classNames([
                    styles.button,
                    styles.rect,
                    styles.share,
                  ])}
                  type="text"
                  data-gd-click="button_click"
                  data-button-name="分享"
                  data-board-id={board?.board_id}
                  shape="round"
                >
                  <HBIcon className={styles.icon} type="hb_share_new" />
                </Button>
              </ShareDropdown>
              <When condition={canEditBoard}>
                <Button
                  type="text"
                  className={styles.button}
                  data-gd-click="button_click"
                  data-button-name="整理"
                  data-board-id={board?.board_id}
                  onClick={goBatch}
                  shape="round"
                >
                  整理
                </Button>
              </When>
            </Then>
            <Else>
              <If condition={canEditBoard}>
                <Then>
                  <ShareDropdown
                    placement="bottomCenter"
                    pics={pics}
                    title={`这个来自「花瓣网」的画板不错哦，有很多${strEllipsis(
                      board?.title,
                      45
                    )}相关的设计灵感，来看看：`}
                  >
                    <Button
                      className={classNames([
                        styles.button,
                        styles.rect,
                        styles.share,
                      ])}
                      type="text"
                      data-gd-click="button_click"
                      data-button-name="分享"
                      data-board-id={board?.board_id}
                      shape="round"
                    >
                      <HBIcon className={styles.icon} type="hb_share_new" />
                    </Button>
                  </ShareDropdown>
                  <UserGuideTooltip
                    needUserId={false}
                    placement="bottom"
                    localStorageKey={"HB_BOARD_EDIT_DRAGSORT"}
                    visible={enableDragSort}
                    title="现已支持拖拽更改采集排序"
                  >
                    <Button
                      type="text"
                      className={styles.button}
                      data-gd-click="button_click"
                      data-button-name="整理"
                      data-board-id={board?.board_id}
                      onClick={goBatch}
                      shape="round"
                    >
                      整理
                    </Button>
                  </UserGuideTooltip>
                  <Button
                    className={styles.button}
                    type="text"
                    onClick={onBoardEdit}
                    data-gd-click="button_click"
                    data-button-name="编辑"
                    data-board-id={board?.board_id}
                    shape="round"
                  >
                    编辑
                  </Button>
                </Then>
                <Else>
                  <ShareDropdown
                    placement="bottomCenter"
                    pics={pics}
                    title={`这个来自「花瓣网」的画板不错哦，有很多${strEllipsis(
                      board?.title,
                      45
                    )}相关的设计灵感，来看看：`}
                  >
                    <Button
                      className={classNames([
                        styles.button,
                        styles.rect,
                        styles.share,
                      ])}
                      type="text"
                      data-gd-click="button_click"
                      data-button-name="分享"
                      data-board-id={board?.board_id}
                      shape="round"
                    >
                      <HBIcon className={styles.icon} type="hb_share_new" />
                    </Button>
                  </ShareDropdown>

                  <FollowButton
                    followed={following}
                    showGrop={
                      board?.user_following !== followType.unIndependent
                    }
                    onClick={onToggleFollow}
                    data-gd-click="button_click"
                    data-button-name={following ? "取消画板" : "关注画板"}
                    data-board-id={board?.board_id}
                    boardInfo={board}
                  />
                </Else>
              </If>
            </Else>
          </If>
        </div>
      </div>

      <div className={styles.bottom}>
        <When condition={board?.description}>
          <pre className={styles.desc}>{board?.description}</pre>
        </When>
        {/* 入榜展示 */}
        <div className={styles.listContent}>
          {listInfo.map((item, index) => {
            return (
              <a
                className={`${styles.card} ${item.href ? styles.hasLink : ""}`}
                key={index}
                onClick={() => open(item.href, item.title)}
                href={item.href}
                target="_blank"
              >
                {item.icon?.length > 0 && (
                  <Image
                    className={styles.cardImg}
                    alt=""
                    src={item.icon[0].url}
                    width={20}
                  ></Image>
                )}

                <div>
                  入选<span className={styles.cardTitle}>{item.title}</span>
                </div>
                {item.href && (
                  <HBIcon type="ic_chevron_right" className={styles.cardIcon} />
                )}
              </a>
            );
          })}
        </div>

        <div className={styles.info}>
          <When condition={!isSpecial && board?.category_id}>
            <div className={styles.category}>
              <span className={styles.categoryTitle}>所属分类：</span>
              <a target="_blank" href={`/discovery/${board?.category_id}`}>
                {board?.category_name ||
                  categories?.find((item) => item.id === board?.category_id)
                    ?.name}
              </a>
            </div>
          </When>

          {board?.is_private === PRIVATETYPE.privacy && (
            <span className={styles.privateIcon}>
              <HBIcon type="ic_lock" />
            </span>
          )}
          <span>{displayNum(board?.pin_count)} 张采集</span>
          <When condition={board?.updated_at}>
            {board?.pin_count > 0 && (
              <div className={styles.time}>
                更新于 {getTimeDuration({ time: board?.updated_at * 1000 })}
              </div>
            )}
          </When>
        </div>

        <div className={styles.more}>
          {members && (
            <UserGuideTooltip
              visible={enableMemberMutate}
              needUserId={false}
              placement="right"
              localStorageKey={LOCAL_STORAGE_KEY.HB_MULTI_BOARD + "_header"}
              title="邀请伙伴，共创灵感"
              align={{ offset: [0, 2] }}
              once
            >
              <BoardMembers
                board={board}
                onUpdateBoard={setBoard}
                onRemoveMember={onRemoveMember}
                onExitBoard={onExitBoard}
                members={members}
                mutateMembers={mutateMembers}
                showMaxNum={3}
                showUserOwner
              />
            </UserGuideTooltip>
          )}
        </div>
      </div>
    </div>
  );
}
